// HeaderUserMenu.jsx — compact dropdown holding ALL personal/admin actions:
// role, version, force update, theme, settings, audit log, change password, logout.
// Replaces the cluttered horizontal stack of chips & buttons in the old header.
// On mobile: tap avatar → dropdown opens. On desktop: shows name + role + chevron.
import React, { useEffect, useRef, useState } from "react";
import { APP_VERSION } from "./lib/version";
import { forceFullReset } from "./lib/pwa";

export default function HeaderUserMenu({ user, role, theme, onToggleTheme, onLogout, onNav, isAdmin, canSettings }) {
  const [open, setOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClickAway = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClickAway);
    document.addEventListener("touchstart", onClickAway);
    return () => {
      document.removeEventListener("mousedown", onClickAway);
      document.removeEventListener("touchstart", onClickAway);
    };
  }, [open]);

  const initials = (user.name || user.username || "?").trim().slice(0, 1).toUpperCase();
  const name = user.name || user.username;

  const go = (tab) => {
    if (typeof onNav === "function") onNav(tab);
    setOpen(false);
  };

  return (
    <div ref={ref} className="hum-wrap" data-testid="hdr-user-menu">
      <button
        type="button"
        className={`hum-trigger ${open ? "hum-trigger-open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        data-testid="hdr-user-trigger"
        aria-expanded={open}
      >
        <span className={`hum-avatar role-bg-${role.key}`}>{role.icon || initials}</span>
        <span className="hum-name-wrap">
          <span className="hum-name">{name}</span>
          <span className="hum-role">{role.lbl}</span>
        </span>
        <span className={`hum-chevron ${open ? "is-open" : ""}`} aria-hidden>▾</span>
      </button>

      {open && (
        <div className="hum-menu" data-testid="hdr-user-dropdown" role="menu">
          <div className="hum-header">
            <div className={`hum-avatar hum-avatar-lg role-bg-${role.key}`}>{role.icon || initials}</div>
            <div>
              <div className="hum-name-big">{name}</div>
              <div className="hum-username">@{user.username}</div>
            </div>
          </div>
          <div className="hum-section">
            <div className="hum-meta-row">
              <span className="hum-meta-label">الدور</span>
              <span className={`role-pill ${role.cls}`}>{role.lbl}</span>
            </div>
            <div className="hum-meta-row">
              <span className="hum-meta-label">نسخة التطبيق</span>
              <span className="hum-version" data-testid="hdr-version">{APP_VERSION}</span>
            </div>
          </div>
          <div className="hum-divider" />
          <button
            type="button"
            className="hum-item hum-item-primary"
            data-testid="btn-force-update"
            disabled={updating}
            onClick={async () => {
              setUpdating(true);
              await forceFullReset();
            }}
          >
            <span>⟳</span>
            <span>{updating ? "جارٍ التحديث…" : "تحديث التطبيق لآخر نسخة"}</span>
          </button>
          <button
            type="button"
            className="hum-item"
            data-testid="hdr-menu-manual"
            onClick={() => go("manual")}
          >
            <span>📖</span>
            <span>دليل الاستخدام</span>
          </button>
          {canSettings && (
            <button
              type="button"
              className="hum-item"
              data-testid="hdr-menu-settings"
              onClick={() => go("st")}
            >
              <span>⚙</span>
              <span>الإعدادات</span>
            </button>
          )}
          {canSettings && (
            <button
              type="button"
              className="hum-item"
              data-testid="hdr-menu-change-password"
              onClick={() => go("st")}
            >
              <span>🔑</span>
              <span>تغيير كلمة السر</span>
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              className="hum-item"
              data-testid="hdr-menu-audit"
              onClick={() => go("audit")}
            >
              <span>📜</span>
              <span>السجلات (النشاط + تحسين النظام)</span>
            </button>
          )}
          <button
            type="button"
            className="hum-item"
            data-testid="btn-theme-toggle"
            onClick={() => { onToggleTheme(); setOpen(false); }}
          >
            <span>{theme === "dark" ? "☀" : "🌙"}</span>
            <span>{theme === "dark" ? "الوضع النهاري" : "الوضع الليلي"}</span>
          </button>
          <button
            type="button"
            className="hum-item hum-item-danger"
            data-testid="btn-logout"
            onClick={() => { onLogout(); setOpen(false); }}
          >
            <span>↩</span>
            <span>تسجيل الخروج</span>
          </button>
        </div>
      )}
    </div>
  );
}
